import { isUnmappedPageId } from "@platform/mpx-quotes-grid";
import { useCallback } from "react";

type UseFootnoteHeaderLabelProps = {
  selectedBpgId?: string;
  selectedBenefitPlanGroup?: { id: string; footnoteHeaderLabel?: string };
  saveFootnoteHeaderLabel: (groupId: string, label: string) => void;
};

type UseFootnoteHeaderLabelResults = {
  footnoteHeaderLabel?: string;
  onFootnoteHeaderLabelChange?: (label: string) => void;
};

/**
 * Resolves the Footnotes row header label for the selected SBS page, and the
 * saver for editing it.
 *
 * The label belongs to the **RFP benefit plan group** — the request-side group
 * the plan selector's submenu scopes the page to. An SBS page also renders many
 * response-side proposal plan groups (one per carrier column), but the header is
 * the row's single left-hand label, so it hangs off the one request-side group
 * rather than any carrier's copy of it.
 *
 * Both the read and the write key off that group's own id, so nothing is
 * inferred from list position and a reorder cannot retarget a saved label.
 *
 * The Unmapped Plans page's group is synthetic (no persisted row), so it gets no
 * saver and its header stays read-only.
 */
export const useFootnoteHeaderLabel = ({
  selectedBpgId,
  selectedBenefitPlanGroup,
  saveFootnoteHeaderLabel,
}: UseFootnoteHeaderLabelProps): UseFootnoteHeaderLabelResults => {
  const footnoteHeaderGroupId = isUnmappedPageId(selectedBpgId)
    ? undefined
    : selectedBenefitPlanGroup?.id;

  const handleFootnoteHeaderLabelChange = useCallback(
    (label: string) => {
      if (!footnoteHeaderGroupId) return;
      saveFootnoteHeaderLabel(footnoteHeaderGroupId, label.trim());
    },
    [footnoteHeaderGroupId, saveFootnoteHeaderLabel],
  );

  return {
    footnoteHeaderLabel: selectedBenefitPlanGroup?.footnoteHeaderLabel,
    onFootnoteHeaderLabelChange: footnoteHeaderGroupId
      ? handleFootnoteHeaderLabelChange
      : undefined,
  };
};
